import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import BrochureGate from "@/components/BrochureGate";
import { 
  Home, CheckCircle, Award, Shield, Calendar, 
  Phone, Mail, MapPin, Star, ArrowRight, Download,
  Clock, Users, Sparkles, TreePine, Flame
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ResidentialPage() {
  const [scrolled, setScrolled] = useState(false);
  const [showBrochureGate, setShowBrochureGate] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    {
      icon: TreePine,
      title: "Tree Trimming & Pruning",
      description: "Expert shaping and maintenance to enhance your property's beauty and tree health.",
      price: "From $350",
    },
    {
      icon: TreePine,
      title: "Tree Removal",
      description: "Safe, professional removal of hazardous or unwanted trees with complete cleanup.",
      price: "From $500",
    },
    {
      icon: TreePine,
      title: "Stump Grinding",
      description: "Complete stump removal leaving your landscape clean and ready for new projects.",
      price: "From $150",
    },
    {
      icon: Flame,
      title: "Fire Safety & Brush Removal",
      description: "Protect your home from wildfire with defensible space creation.",
      price: "From $400",
    },
    {
      icon: Award,
      title: "ISA Arborist Assessment",
      description: "Comprehensive tree health evaluation by certified professionals.",
      price: "From $650",
      note: "Paid service",
    },
    {
      icon: Clock,
      title: "24/7 Emergency Service",
      description: "Immediate response for storm damage during monsoon season.",
      price: "Call for Quote",
    },
  ];

  const premiumAreas = [
    {
      city: "Paradise Valley",
      description: "Arizona's most exclusive residential community",
      zips: ["85253"],
      avgHome: "$3M+",
    },
    {
      city: "Scottsdale - North",
      description: "DC Ranch, Silverleaf, Desert Mountain",
      zips: ["85255", "85262", "85266"],
      avgHome: "$2M+",
    },
    {
      city: "Scottsdale - Central",
      description: "Gainey Ranch, McCormick Ranch, Kierland",
      zips: ["85258", "85259", "85260"],
      avgHome: "$1M+",
    },
    {
      city: "Phoenix - Arcadia",
      description: "Historic luxury neighborhood",
      zips: ["85018", "85016"],
      avgHome: "$1.5M+",
    },
    {
      city: "Phoenix - Biltmore",
      description: "Arizona Biltmore Estates",
      zips: ["85016", "85014"],
      avgHome: "$1.2M+",
    },
    {
      city: "Fountain Hills",
      description: "Mountain views and golf communities",
      zips: ["85268", "85269"],
      avgHome: "$800K+",
    },
    {
      city: "Cave Creek",
      description: "Desert foothills luxury",
      zips: ["85331"],
      avgHome: "$900K+",
    },
    {
      city: "Carefree",
      description: "Exclusive desert community",
      zips: ["85377"],
      avgHome: "$1.5M+",
    },
  ];

  const testimonials = [
    {
      quote: "Celebrity Tree Service transformed our backyard. Professional, punctual, and the results exceeded our expectations.",
      author: "Sarah M.",
      location: "Paradise Valley",
    },
    {
      quote: "After the monsoon damaged several trees, they responded within hours. Saved our property from further damage.",
      author: "Michael R.",
      location: "Scottsdale",
    },
    {
      quote: "The ISA Arborist assessment saved two of our mature trees that we thought needed removal. Worth every penny.",
      author: "Jennifer L.",
      location: "Arcadia",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Residential Tree Service | Celebrity Tree Service | Scottsdale, Paradise Valley, Phoenix AZ</title>
        <meta name="description" content="Premium residential tree care in Scottsdale, Paradise Valley, and Phoenix Arizona. ISA Certified Arborists serving Arizona's finest neighborhoods. Tree trimming, removal, and fire safety." />
        <meta name="keywords" content="residential tree service, Scottsdale tree trimming, Paradise Valley tree removal, Phoenix tree care, luxury home tree service, Arizona arborist" />
        <link rel="canonical" href="https://paradisevalleytreeservice.com/residential" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F9F5]" data-testid="residential-page">
        <Navigation scrolled={scrolled} />

        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] flex items-center pt-32"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/816198/pexels-photo-816198.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/70 to-transparent" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/40 px-4 py-2 mb-6">
                <Home className="w-4 h-4 text-[#10B981]" />
                <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider">
                  Residential Tree Care
                </span>
              </div>
              
              <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Protecting Arizona's
                <br />
                <span className="text-[#10B981]">Finest Homes</span>
              </h1>
              
              <p className="text-white/80 text-xl mb-8 max-w-2xl">
                Your home deserves the same level of care as Arizona's premier estates. 
                ISA Certified Arborists delivering expert tree care with old-world 
                craftsmanship and modern expertise.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-6 uppercase tracking-[0.15em] text-sm font-bold"
                >
                  <a href="tel:4803517775">
                    <Phone className="w-4 h-4 mr-2" />
                    Call (480) 351-7775
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-8 py-6 uppercase tracking-[0.15em] text-sm font-bold"
                >
                  <a href="/#contact">Request Free Estimate</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-[#0A1628] py-8 border-y border-[#10B981]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { icon: Award, label: "ISA Certified", sub: "Arborists" },
                { icon: Shield, label: "Fully Insured", sub: "Complete Coverage" },
                { icon: Clock, label: "24/7 Emergency", sub: "Monsoon Ready" },
                { icon: Users, label: "Since 2015", sub: "Trusted by 1,500+" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-center gap-3">
                  <item.icon className="w-8 h-8 text-[#10B981]" />
                  <div className="text-left">
                    <p className="text-white font-bold">{item.label}</p>
                    <p className="text-white/60 text-sm">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Our Services
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Residential Tree Care Services
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
                Comprehensive tree care solutions for Arizona's luxury homes. 
                Every service backed by ISA certified expertise.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, idx) => (
                <div 
                  key={idx}
                  className="bg-[#F8F6F1] p-8 border-l-4 border-[#10B981] hover:shadow-lg transition-shadow"
                  data-testid={`service-card-${idx}`}
                >
                  <service.icon className="w-10 h-10 text-[#10B981] mb-4" />
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#0A1628] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-[#64748B] mb-4">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#10B981] font-bold">{service.price}</span>
                    {service.note && (
                      <span className="text-xs text-[#D4A853] font-semibold uppercase">{service.note}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-[#64748B] mb-6">
                Not sure which service you need? Call for a free phone consultation.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="tel:4803517775">Call (480) 351-7775</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-24 bg-[#FDFCF9]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#10B981] text-xs tracking-[0.3em] uppercase mb-4">
                Service Areas
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                Premium Neighborhoods We Serve
              </h2>
              <p className="text-[#64748B] max-w-2xl mx-auto">
                Proudly serving Arizona's most distinguished residential communities 
                within 15 miles of Scottsdale and Paradise Valley.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {premiumAreas.map((area, idx) => (
                <div key={idx} className="bg-white p-6 border border-[#10B981]/20 hover:border-[#10B981]/40 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5 text-[#10B981]" />
                    <h3 className="font-bold text-[#0A1628]">{area.city}</h3>
                  </div>
                  <p className="text-[#64748B] text-sm mb-3">{area.description}</p>
                  <p className="text-xs text-[#64748B] mb-2">Zip Codes: {area.zips.join(", ")}</p>
                  <p className="text-[#D4A853] text-sm font-semibold">Avg. Home: {area.avgHome}</p>
                </div>
              ))}
            </div>

            {/* California Notice */}
            <div className="bg-gradient-to-r from-[#0A1628] to-[#1E3A5F] p-8 text-center">
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-white mb-4">
                Also Serving California
              </h3>
              <p className="text-white/80 mb-6 max-w-2xl mx-auto">
                Have a second home in California? Celebrity Tree Service now offers 
                residential tree care in select California locations. Contact us for availability.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="tel:4803517775">Inquire About California</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-['Cinzel'] text-[#D4A853] text-xs tracking-[0.3em] uppercase mb-4">
                Client Testimonials
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6">
                What Homeowners Say
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, idx) => (
                <div key={idx} className="bg-[#F8F6F1] p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[#D4A853]" fill="#D4A853" />
                    ))}
                  </div>
                  <p className="text-[#64748B] italic mb-6">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-bold text-[#0A1628]">{testimonial.author}</p>
                    <p className="text-[#10B981] text-sm">{testimonial.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fire Safety CTA */}
        <section className="py-16 bg-gradient-to-r from-[#DC2626]/10 to-[#DC2626]/5 border-y border-[#DC2626]/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Flame className="w-12 h-12 text-[#DC2626] mx-auto mb-4" />
            <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#0A1628] mb-4">
              Is Your Home Fire-Safe?
            </h3>
            <p className="text-[#64748B] text-lg mb-6 max-w-2xl mx-auto">
              Arizona's desert climate creates unique fire hazards. Our fire safety 
              assessments can reduce your wildfire risk by up to 80% and may improve 
              your insurance eligibility.
            </p>
            <Button
              asChild
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-none px-10 py-5 uppercase tracking-[0.15em] text-sm font-bold"
            >
              <a href="/#fire-safety">Learn About Fire Safety</a>
            </Button>
          </div>
        </section>

        {/* Brochure Download */}
        <section className="py-24 bg-[#FDFCF9]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#0A1628] mb-6">
              Download Our Residential Guide
            </h2>
            <p className="text-[#64748B] text-lg mb-8">
              Get our comprehensive guide to residential tree care, including seasonal 
              maintenance tips, fire safety checklists, and pricing information.
            </p>
            <Button
              onClick={() => setShowBrochureGate(true)}
              className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              data-testid="download-brochure-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Free Guide
            </Button>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Landscape?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Join over 1,500 Arizona homeowners who trust Celebrity Tree Service 
              for their tree care needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="tel:4803517775">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-10 py-6 uppercase tracking-[0.15em] text-sm font-bold"
              >
                <a href="/#contact">Request Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* Brochure Gate Modal */}
      {showBrochureGate && (
        <BrochureGate 
          type="residential" 
          onClose={() => setShowBrochureGate(false)} 
        />
      )}
    </>
  );
}
